import { ModeManager } from './core/ModeManager';
import { defaultLevel, draftFromLevel } from './icons-match/schema';
import type { IconsMatchLevel, EditorDraft } from './icons-match/schema';
import { EditorView } from './icons-match/EditorView';
import { PlayView } from './icons-match/PlayView';
import { createProjectStorage } from './core/storage/createProjectStorage';
import type { ProjectStorage } from './core/storage/ProjectStorage';
import { storageConfig } from './core/storage/storageConfig';
import { StorageError } from './core/storage/RemoteProjectStorage';
import { buildLoginUrl, captureSessionFromHash, clearSession } from './core/auth/session';

// Capture a fresh OAuth session from the URL hash before any storage calls.
captureSessionFromHash();

// ── Storage ────────────────────────────────────────────────────
let storage: ProjectStorage<IconsMatchLevel>;

// ── Storage error messages ─────────────────────────────────────
const ERROR_MESSAGES: Record<string, string> = {
  TOKEN_MISSING:        'GitHub token не настроен на сервере',
  REPO_NOT_FOUND:       'Репозиторий не найден',
  ACCESS_DENIED:        'Нет доступа к репозиторию',
  CONFLICT:             'Конфликт записи — обнови страницу и попробуй снова',
  BRANCH_CREATE_FAILED: 'Не удалось создать ветку ld-content',
  WRITE_FAILED:         'Ошибка записи в GitHub',
  READ_FAILED:          'Ошибка чтения из GitHub',
  NETWORK_ERROR:        'Нет подключения к сети',
  PROJECT_NOT_FOUND:    'Конфигурация проекта не найдена',
  UNAUTHORIZED:         'Требуется вход через GitHub',
};

function storageErrorMessage(e: unknown): string {
  if (e instanceof StorageError) return ERROR_MESSAGES[e.code] ?? `Ошибка: ${e.message}`;
  return `Ошибка: ${String(e)}`;
}

// ── Local drafts (localStorage) ────────────────────────────────
// Stores EditorDraft — may contain neutral tiles.
const DRAFT = 'im-draft:';
function saveDraft(name: string, draft: EditorDraft): void {
  localStorage.setItem(`${DRAFT}draft:${name}`, JSON.stringify(draft));
}
function loadDraft(name: string): EditorDraft | null {
  const raw = localStorage.getItem(`${DRAFT}draft:${name}`);
  if (!raw) return null;
  try { return JSON.parse(raw) as EditorDraft; } catch { return null; }
}
function removeDraft(name: string): void {
  localStorage.removeItem(`${DRAFT}draft:${name}`);
}
function saveDraftLineup(names: string[]): void {
  localStorage.setItem(`${DRAFT}lineup`, JSON.stringify(names));
}
function loadDraftLineup(): string[] | null {
  const raw = localStorage.getItem(`${DRAFT}lineup`);
  return raw ? JSON.parse(raw) as string[] : null;
}

// ── State ──────────────────────────────────────────────────────
const modeManager = new ModeManager();
let lineup: string[] = [];
// Committed levels — synced with remote. Used for play mode and commit base.
let levels: Map<string, IconsMatchLevel> = new Map();
let currentLevelName: string | null = null;
const newLevels   = new Set<string>();
const dirtyLevels = new Set<string>();

// ── Views ──────────────────────────────────────────────────────
const editorMain = document.getElementById('editor-main')!;
const playMain   = document.getElementById('play-main')!;

const editorView = new EditorView(editorMain, (draft: EditorDraft) => {
  if (!currentLevelName) return;
  saveDraft(currentLevelName, draft);
  if (!newLevels.has(currentLevelName)) dirtyLevels.add(currentLevelName);
  renderEditorLineup();
});

const playView = new PlayView(
  playMain,
  () => { playStatus.textContent = 'Level complete!'; },
  () => { playStatus.textContent = 'Level failed — try again.'; },
);

// ── Init ───────────────────────────────────────────────────────
async function initStorage(): Promise<void> {
  const draftLineup = loadDraftLineup();

  if (draftLineup && draftLineup.length > 0) {
    lineup = draftLineup;
    // Mark levels that have local drafts as dirty/new (remote sync will refine this)
    for (const name of lineup) {
      if (loadDraft(name) !== null) newLevels.add(name);
    }
    await openLevel(lineup[0]);
    renderEditorLineup();
    renderPlayLineup();
    syncLineupTextarea();
    void Promise.all([
      syncWithRemote().then(() => {
        // After remote sync, reload the current level if it has no local draft
        // (the editor may have shown a blank default before remote data arrived).
        if (currentLevelName && !loadDraft(currentLevelName)) {
          const remote = levels.get(currentLevelName);
          if (remote) editorView.loadLevel(remote);
        }
      }),
      syncImages(),
    ]);
  } else {
    await Promise.all([syncWithRemote(), syncImages()]);
    if (lineup.length === 0) {
      await createNewLevel();
    } else {
      await openLevel(lineup[0]);
      renderEditorLineup();
      renderPlayLineup();
      syncLineupTextarea();
    }
  }
}

async function syncImages(): Promise<void> {
  try {
    const paths = await storage.listImages(); // e.g. ["images/star.png", ...]
    const map = new Map<string, string>();
    for (const path of paths) {
      const filename = path.replace(/^images\//, '');
      const imageId  = filename.replace(/\.png$/i, '');
      map.set(imageId, storage.getImageUrl(path));
    }
    editorView.setImageMap(map);
    playView.setImageMap(map);
  } catch {
    // Images are optional — silently ignore if unavailable
  }
}

async function syncWithRemote(): Promise<void> {
  try {
    const [remoteLineup, remoteNames] = await Promise.all([
      storage.loadLineup(),
      storage.listLevels(),
    ]);
    const remoteSet = new Set([...remoteLineup, ...remoteNames]);

    for (const name of remoteLineup) {
      if (!lineup.includes(name)) lineup.push(name);
    }

    const toFetch = [...remoteSet].filter(name => !levels.has(name));
    await Promise.all(toFetch.map(async name => {
      try { levels.set(name, await storage.loadLevel(name)); } catch { /* missing */ }
    }));

    newLevels.clear();
    dirtyLevels.clear();
    for (const name of lineup) {
      const hasDraft  = loadDraft(name) !== null;
      const hasRemote = levels.has(name);
      if (hasDraft) {
        if (hasRemote) dirtyLevels.add(name);
        else newLevels.add(name);
      } else if (!hasRemote) {
        // In lineup but file missing from remote — treat as uncommitted
        newLevels.add(name);
      }
    }

    renderEditorLineup();
    renderPlayLineup();
    syncLineupTextarea();
  } catch (e) {
    console.warn('[storage] Remote sync failed:', e);
    if (handleAuthError(e)) return;
    const code = (e instanceof StorageError) ? e.code : '';
    if (code === 'NETWORK_ERROR') {
      flashMsg(storageErrorMessage(e), true);
    } else {
      storageErrorBanner.textContent = storageErrorMessage(e);
      storageErrorBanner.classList.remove('hidden');
    }
  }
}

// ── Level management ───────────────────────────────────────────
function nextAvailableName(): string {
  const existing = new Set(lineup);
  let i = 1;
  while (existing.has(`lvl_${String(i).padStart(4, '0')}`)) i++;
  return `lvl_${String(i).padStart(4, '0')}`;
}

async function createNewLevel(): Promise<void> {
  const name = nextAvailableName();
  const level: IconsMatchLevel = { ...defaultLevel(name), id: name };
  levels.set(name, level);
  newLevels.add(name);
  saveDraft(name, draftFromLevel(level)); // persist so refresh restores state
  lineup.push(name);
  saveDraftLineup(lineup);
  await openLevel(name);
  renderEditorLineup();
  renderPlayLineup();
  syncLineupTextarea();
}

async function openLevel(name: string): Promise<void> {
  currentLevelName = name;
  const draft = loadDraft(name);
  if (draft) {
    editorView.loadDraft(draft);
  } else {
    const committed = levels.get(name);
    // Use committed data if available; otherwise show a blank default.
    // Do NOT write the default into `levels` — that map holds only remote data
    // and must stay empty so syncWithRemote fetches the real file from GitHub.
    editorView.loadLevel(committed ?? { ...defaultLevel(name), id: name });
  }
  if (modeManager.current === 'play') {
    const level = levels.get(name);
    if (level) playView.loadLevel(level);
  }
  updateNameInput();
  renderEditorLineup();
  renderPlayLineup();
}

async function renameCurrentLevel(newName: string): Promise<void> {
  const trimmed = newName.trim();
  if (!currentLevelName) return;
  if (!trimmed || trimmed === currentLevelName) {
    levelNameInput.value = currentLevelName;
    return;
  }
  if (!/^\S+$/.test(trimmed)) { setNameError('Без пробелов'); levelNameInput.value = currentLevelName; return; }
  if (lineup.includes(trimmed)) { setNameError('Имя занято'); levelNameInput.value = currentLevelName; return; }

  const oldName = currentLevelName;

  // Rename draft
  const existingDraft = loadDraft(oldName);
  if (existingDraft) {
    existingDraft.levelId = trimmed;
    saveDraft(trimmed, existingDraft);
    removeDraft(oldName);
  }

  // Rename in committed levels map
  const oldLevel = levels.get(oldName);
  if (oldLevel) {
    const renamedLevel: IconsMatchLevel = { ...oldLevel, id: trimmed };
    levels.delete(oldName);
    levels.set(trimmed, renamedLevel);
    try {
      await storage.saveLevel(trimmed, renamedLevel);
      await storage.deleteLevel(oldName);
    } catch (e) {
      flashMsg(storageErrorMessage(e), true);
    }
  } else {
    levels.delete(oldName);
  }

  if (newLevels.delete(oldName))   newLevels.add(trimmed);
  if (dirtyLevels.delete(oldName)) dirtyLevels.add(trimmed);

  const idx = lineup.indexOf(oldName);
  if (idx >= 0) lineup[idx] = trimmed;
  currentLevelName = trimmed;

  try { await storage.saveLineup(lineup); } catch { /* offline */ }

  setNameError('');
  updateNameInput();
  renderEditorLineup();
  renderPlayLineup();
  syncLineupTextarea();
}

async function moveLevel(name: string, newPos: number): Promise<void> {
  const currentIdx = lineup.indexOf(name);
  if (currentIdx < 0) return;
  const targetIdx = Math.max(0, Math.min(lineup.length - 1, newPos - 1));
  lineup.splice(currentIdx, 1);
  lineup.splice(targetIdx, 0, name);
  await storage.saveLineup(lineup);
  renderEditorLineup();
  renderPlayLineup();
  syncLineupTextarea();
}

// ── DOM refs ───────────────────────────────────────────────────
const modeTabs           = document.querySelectorAll<HTMLButtonElement>('.mode-tab');
const editorLayout       = document.getElementById('editor-layout')!;
const playLayout         = document.getElementById('play-layout')!;
const levelNameInput     = document.getElementById('level-name-input') as HTMLInputElement;
const levelNameError     = document.getElementById('level-name-error')!;
const btnNewLevel        = document.getElementById('btn-new-level')!;
const btnSave            = document.getElementById('btn-save')!;
const btnCommitAll       = document.getElementById('btn-commit-all') as HTMLButtonElement;
const btnRestore         = document.getElementById('btn-restore')!;
const validationMsg      = document.getElementById('validation-msg')!;
const storageErrorBanner = document.getElementById('storage-error-banner')!;
const editorLineupList   = document.getElementById('editor-lineup-list')!;
const playLineupList     = document.getElementById('play-lineup-list')!;
const btnRestart         = document.getElementById('btn-restart')!;
const playStatus         = document.getElementById('play-status')!;
const lineupTextarea     = document.getElementById('lineup-textarea') as HTMLTextAreaElement;
const lineupValidation   = document.getElementById('lineup-validation')!;
const btnApplyLineup     = document.getElementById('btn-apply-lineup')!;
const loginOverlay       = document.getElementById('login-overlay')!;
const btnLogin           = document.getElementById('btn-login')!;

// ── Auth ───────────────────────────────────────────────────────
/**
 * If the error is an expired/missing session, drop the stale session and show
 * the login overlay. Returns true when the error was auth-related. No
 * auto-redirect — the user clicks the button (avoids redirect loops).
 */
function handleAuthError(e: unknown): boolean {
  if (e instanceof StorageError && e.code === 'UNAUTHORIZED') {
    clearSession();
    loginOverlay.classList.remove('hidden');
    return true;
  }
  return false;
}

btnLogin.addEventListener('click', () => {
  window.location.href = buildLoginUrl(storageConfig.apiBaseUrl);
});

// ── Mode switching ─────────────────────────────────────────────
modeManager.onChange(({ to }) => {
  modeTabs.forEach(btn =>
    btn.classList.toggle('active', btn.dataset['mode'] === to));
  editorLayout.classList.toggle('hidden', to !== 'editor');
  playLayout.classList.toggle('hidden', to !== 'play');
  editorView.setActive(to === 'editor');

  if (to === 'play') {
    const result = editorView.getLevel();
    if (result.ok) {
      playView.loadLevel(result.level);
    } else {
      playStatus.textContent = 'Fix neutral tiles before playing.';
    }
    playView.reset();
  }
});

modeTabs.forEach(btn => {
  btn.addEventListener('click', () => {
    modeManager.switchTo(btn.dataset['mode'] as 'editor' | 'play' | 'generator');
  });
});

// ── Level name field ───────────────────────────────────────────
function updateNameInput(): void {
  levelNameInput.value = currentLevelName ?? '';
  setNameError('');
}

function setNameError(msg: string): void {
  levelNameError.textContent = msg;
  levelNameInput.classList.toggle('error', !!msg);
}

levelNameInput.addEventListener('blur', () => { void renameCurrentLevel(levelNameInput.value); });
levelNameInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') levelNameInput.blur();
  else if (e.key === 'Escape') { levelNameInput.value = currentLevelName ?? ''; setNameError(''); levelNameInput.blur(); }
});

// ── New level ──────────────────────────────────────────────────
btnNewLevel.addEventListener('click', () => { void createNewLevel(); });

// ── Commit ─────────────────────────────────────────────────────
btnSave.addEventListener('click', async () => {
  if (!currentLevelName) return;
  const result = editorView.getLevel();
  if (!result.ok) {
    flashMsg(result.errors.join(' · '), true);
    return;
  }
  const level = { ...result.level, id: currentLevelName };
  levels.set(currentLevelName, level);
  try {
    await storage.saveLevel(currentLevelName, level);
    newLevels.delete(currentLevelName);
    dirtyLevels.delete(currentLevelName);
    removeDraft(currentLevelName);
    renderEditorLineup();
    flashMsg('Committed.');
  } catch (e) {
    if (handleAuthError(e)) return;
    flashMsg(storageErrorMessage(e), true);
  }
});

// ── Restore ────────────────────────────────────────────────────
btnRestore.addEventListener('click', async () => {
  if (!currentLevelName) return;
  try {
    const remote = await storage.loadLevel(currentLevelName);
    levels.set(currentLevelName, remote);
    editorView.loadLevel(remote);
    removeDraft(currentLevelName);
    dirtyLevels.delete(currentLevelName);
    renderEditorLineup();
    flashMsg('Restored from remote.');
  } catch (e) {
    if (handleAuthError(e)) return;
    flashMsg('No remote version — not committed yet.');
  }
});

// ── Commit All ─────────────────────────────────────────────────
btnCommitAll.addEventListener('click', async () => {
  const pending = [...new Set([...newLevels, ...dirtyLevels])];
  if (pending.length === 0) { flashMsg('Nothing to commit.'); return; }

  btnCommitAll.disabled = true;
  let done = 0;
  let commitError: unknown = null;
  try {
    for (const name of pending) {
      let level: IconsMatchLevel | undefined;
      if (name === currentLevelName) {
        const r = editorView.getLevel();
        if (!r.ok) {
          commitError = new Error(`${name}: ${r.errors.join(', ')}`);
          break;
        }
        level = { ...r.level, id: name };
      } else {
        level = levels.get(name);
      }
      if (!level) continue;
      try {
        await storage.saveLevel(name, level);
        levels.set(name, level);
        newLevels.delete(name);
        dirtyLevels.delete(name);
        removeDraft(name);
        done++;
      } catch (e) { commitError = e; break; }
    }
    if (!commitError) await storage.saveLineup(lineup);
  } finally {
    btnCommitAll.disabled = false;
  }
  renderEditorLineup();
  if (commitError) {
    if (!handleAuthError(commitError)) flashMsg(storageErrorMessage(commitError), true);
  } else {
    flashMsg(`Committed ${done} level${done !== 1 ? 's' : ''}.`);
  }
});

// ── Play controls ──────────────────────────────────────────────
btnRestart.addEventListener('click', () => {
  playView.reset();
  playStatus.textContent = '';
});

// ── Editor lineup list ─────────────────────────────────────────
function renderEditorLineup(): void {
  editorLineupList.innerHTML = '';
  lineup.forEach((name) => {
    const exists = levels.has(name) || loadDraft(name) !== null;
    const isActive = name === currentLevelName;

    const item = document.createElement('div');
    item.className = [
      'lineup-item',
      isActive  ? 'active'      : '',
      !exists   ? 'missing'     : '',
      newLevels.has(name)   ? 'level-new'   : '',
      dirtyLevels.has(name) ? 'level-dirty' : '',
    ].filter(Boolean).join(' ');

    const nameSpan = document.createElement('span');
    nameSpan.className = 'lineup-name';
    nameSpan.textContent = exists ? name : `${name} ⚠`;
    item.appendChild(nameSpan);

    if (exists) item.addEventListener('click', () => { void openLevel(name); });
    editorLineupList.appendChild(item);
  });
}

// ── Play lineup list ───────────────────────────────────────────
function renderPlayLineup(): void {
  playLineupList.innerHTML = '';
  lineup.forEach((name, idx) => {
    const exists = levels.has(name) || loadDraft(name) !== null;
    const isActive = name === currentLevelName;

    const item = document.createElement('div');
    item.className = ['lineup-item', isActive ? 'active' : '', !exists ? 'missing' : ''].filter(Boolean).join(' ');

    const posInput = document.createElement('input');
    posInput.type = 'number';
    posInput.className = 'lineup-pos';
    posInput.value = String(idx + 1);
    posInput.min = '1';
    posInput.max = String(lineup.length);
    posInput.addEventListener('click', e => e.stopPropagation());
    posInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') posInput.blur();
      if (e.key === 'Escape') { posInput.value = String(lineup.indexOf(name) + 1); posInput.blur(); }
    });
    posInput.addEventListener('blur', () => {
      const newPos = parseInt(posInput.value);
      if (!isNaN(newPos) && newPos !== lineup.indexOf(name) + 1) void moveLevel(name, newPos);
      else posInput.value = String(lineup.indexOf(name) + 1);
    });

    const nameSpan = document.createElement('span');
    nameSpan.className = 'lineup-name';
    nameSpan.textContent = exists ? name : `${name} ⚠`;

    item.appendChild(posInput);
    item.appendChild(nameSpan);

    item.addEventListener('dblclick', async () => {
      if (!exists) return;
      await openLevel(name);
      if (modeManager.current !== 'play') {
        modeManager.switchTo('play');
      } else {
        // already in play — reload level
        const result = editorView.getLevel();
        if (result.ok) { playView.loadLevel(result.level); playView.reset(); }
        playStatus.textContent = '';
      }
    });

    playLineupList.appendChild(item);
  });
}

// ── Lineup textarea ────────────────────────────────────────────
function parseLineupText(text: string): string[] {
  return text.split('\n').map(line => {
    const t = line.trim();
    if (!t) return null;
    const withNum = t.match(/^\d+[\t ]+(\S+)$/);
    if (withNum) return withNum[1];
    if (/^\S+$/.test(t)) return t;
    return null;
  }).filter((n): n is string => n !== null);
}

function validateLineupTextarea(): void {
  const parsed = parseLineupText(lineupTextarea.value);
  const allKnown = new Set([...levels.keys(), ...lineup]);
  const msgs: { warn: boolean; text: string }[] = [];
  const seen = new Set<string>();
  for (const name of parsed) {
    if (seen.has(name)) msgs.push({ warn: true,  text: `${name} — дубликат` });
    else { seen.add(name); if (!allKnown.has(name)) msgs.push({ warn: true, text: `${name} — не существует` }); }
  }
  for (const name of allKnown) {
    if (!seen.has(name)) msgs.push({ warn: false, text: `${name} — не в линейке` });
  }
  // textContent, not innerHTML: level names come from remote data and must
  // never be parsed as HTML.
  lineupValidation.replaceChildren(...msgs.map(m => {
    const div = document.createElement('div');
    div.className = m.warn ? 'lineup-warn' : 'lineup-info';
    div.textContent = `${m.warn ? '⚠' : 'ℹ'} ${m.text}`;
    return div;
  }));
}

function syncLineupTextarea(): void {
  lineupTextarea.value = lineup.map((name, i) => `${i + 1}\t${name}`).join('\n');
  validateLineupTextarea();
}

lineupTextarea.addEventListener('input', validateLineupTextarea);

btnApplyLineup.addEventListener('click', async () => {
  lineup = [...new Set(parseLineupText(lineupTextarea.value))];
  saveDraftLineup(lineup);
  await storage.saveLineup(lineup);
  renderEditorLineup();
  renderPlayLineup();
  syncLineupTextarea();
});

// ── Flash message ──────────────────────────────────────────────
function flashMsg(msg: string, isError = false): void {
  validationMsg.textContent = msg;
  validationMsg.classList.toggle('error-msg', isError);
  clearTimeout((flashMsg as { _t?: ReturnType<typeof setTimeout> })._t);
  (flashMsg as { _t?: ReturnType<typeof setTimeout> })._t =
    setTimeout(() => { validationMsg.textContent = ''; validationMsg.classList.remove('error-msg'); }, isError ? 6000 : 2000);
}

// ── Boot ───────────────────────────────────────────────────────
async function boot(): Promise<void> {
  storage = createProjectStorage<IconsMatchLevel>(storageConfig);
  if (storageConfig.mode === 'remote') {
    editorView.setImageUrlResolver(imageId => storage.getImageUrl(`images/${imageId}.png`));
  }
  void initStorage();
}

void boot();
